<template>
  <div class="view content">
    <div class="container content">
      <div class="header">Formulaire de contact</div>
      <Transition name="slide">
        <div v-if="confirmation" class="confirmation" :class="{ error: error }">
          {{ confirmation }}
        </div>
      </Transition>
      <div class="form">
        <div class="column contact">
          <div class="form-container">
            <div class="input-title">{{ $t('fullName') }}<span class="required">*</span></div>
            <input v-model="fullName" type="text" class="input" :placeholder="$t('fullName')" />
          </div>

          <div class="form-container">
            <div class="input-title">{{ $t('emailAddress') }}<span class="required">*</span></div>
            <input v-model="emailAddress" type="text" class="input" :placeholder="$t('emailAddress')" />
          </div>

          <div class="form-container">
            <div class="input-title">{{ $t('company') }}</div>
            <input v-model="company" type="text" class="input" :placeholder="$t('company')" />
          </div>

          <div class="form-container">
            <div class="input-title">{{ $t('phone') }}<span class="required">*</span></div>
            <input v-model="phone" type="text" class="input" :placeholder="$t('phone')" />
          </div>
        </div>

        <div class="column messages">
          <div class="form-container">
            <div class="input-title">{{ $t('subject') }}<span class="required">*</span></div>
            <input v-model="subject" type="text" class="input" :placeholder="$t('subject')" />
          </div>

          <div class="form-container">
            <div class="input-title">{{ $t('message') }}<span class="required">*</span></div>
            <textarea v-model="message" class="input" placeholder="Texte" />
          </div>
        </div>
      </div>
      <div class="mandatory-field"><span class="required">*</span>{{ $t('mandatoryField') }}</div>

      <div class="checkbox">
        <div class="check-box" @click="acceptRGPD = !acceptRGPD">
          <div class="checkable" :class="{ checked: acceptRGPD }" />
          <div class="value" v-html="$t('rgpd.textAgreement')"></div>
        </div>
      </div>

      <div class="checkbox">
        <div class="check-box" @click="acceptCGV = !acceptCGV">
          <div class="checkable" :class="{ checked: acceptCGV }" />
          <div class="value" v-html="$t('rgpd.cguvTextAgreement')"></div>
        </div>
      </div>

      <div class="footer-container">
        <div class="button main-button sent" :class="{ disabled: !formValid }" @click="submit">
          {{ $t('send') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import config from '@/config';

export default {
  data() {
    return {
      fullName: '',
      emailAddress: '',
      company: '',
      phone: '',
      subject: '',
      message: '',
      loading: false,
      confirmation: '',
      error: false,
      acceptRGPD: false,
      acceptCGV: false,
      captchaResponse: null,
    };
  },

  computed: {
    config() {
      return config;
    },

    formValid() {
      return this.fullName && this.emailAddress && this.subject && this.message && this.phone && this.acceptCGV && this.acceptRGPD;
    },
  },

  methods: {
    async submit() {
      await this.$recaptchaLoaded?.();

      try {
        this.captchaResponse = await this.$recaptcha('contact');
        this.sendMessage();
      } catch (err) {
        console.error('recaptcha error', err);
        this.confirmation = this.$t('messageNotSent');
        this.error = true;
      }
    },

    async sendMessage() {
      if (this.loading || !this.formValid) return;

      const user = {
        fullName: this.fullName,
        companyName: this.company,
        emailAddress: this.emailAddress,
        phoneNumber: this.phone,
      };
      const message = {
        subject: this.subject,
        body: this.message,
      };
      try {
        this.loading = true;
        await this.$store.dispatch('contactInfogreffe', { user, message, captchaResponse: this.captchaResponse });
        this.confirmation = this.$t('messageSent');
        this.fullName = '';
        this.emailAddress = '';
        this.company = '';
        this.phone = '';
        this.subject = '';
        this.message = '';
        this.acceptCGV = false;
        this.acceptRGPD = false;
        this.error = false;
        setTimeout(() => {
          this.confirmation = '';
        }, 3000);
      } catch {
        this.error = true;
        this.confirmation = this.$t('messageNotSent');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
.view {
  display: flex;
  justify-content: center;

  padding-top: 90px;
}

.container {
  margin: 96px 0;
  max-width: 987px;
}

.footer-container {
  display: flex;
  justify-content: center;
  margin: 36px 0;

  .button {
    max-width: 184px;

    &.disabled {
      pointer-events: none;
      opacity: 0.3;
    }
  }
}

.header {
  font-weight: 500;
  font-size: 22px;
  line-height: 26px;

  display: flex;
  align-items: center;

  color: colors.$darkblue-color;

  margin-bottom: 24px;
}

.confirmation {
  position: absolute;
  top: 40px;
  color: colors.$green-color;

  &.error {
    color: colors.$red-color;
  }
}

.slide-enter-active,
.slide-leave-active {
  opacity: 1;
  left: 0;
  transition: all 0.8s;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  left: -90px;
}

.form {
  display: flex;
  flex-direction: row;

  .column {
    width: 100%;

    &:first-child {
      margin-right: 12px;
    }

    &:last-child {
      margin-left: 12px;
    }
  }
}

.form-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .required {
    margin-left: 5px;
    color: colors.$blue-color;
  }

  .input-title {
    color: colors.$darkblue-color;
    font-weight: 500;
    font-size: 16px;
    margin: 24px 0 10px 0;
  }

  .input {
    width: 100%;
    height: 48px;
    margin: 0 0 16px;
    padding: 12px 16px;
    box-sizing: border-box;
    color: colors.$text-color;
    border: solid 1px colors.$lightgrey-color;
    border-radius: 4px;
    background: colors.$white-color;

    &:last-child {
      margin: 0;
    }

    &::placeholder {
      color: colors.$lightgrey-color;
    }
  }
}

.checkbox {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 48px;
  margin: 24px 0 16px;
  padding: 12px 16px;
  box-sizing: border-box;
  color: colors.$text-color;
  background: colors.$white-color;
  border: none;
  transition: 0.3s ease;

  .value {
    font-size: 14px;
  }

  .check-box {
    cursor: pointer;
    display: flex;
    align-items: center;
    flex-grow: 1;
    transition: all 0.3s;

    :deep(.value) {
      margin-left: 16px;

      a {
        color: colors.$blue-color;
        text-decoration: underline;
        cursor: pointer;
        transition: 0.3s;

        &:hover {
          color: colors.$green-color;
        }
      }
    }

    .checkable {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      $size: 16px;
      width: $size;
      height: $size;
      border-radius: 4px;
      border: solid 1px colors.$lightgrey-color;
      transition: 0.2s ease-out;

      &::after {
        display: block;
        position: absolute;
        content: '';
        border: solid white;
        border-width: 0 2px 2px 0;
        margin: -1px 0 0;
        padding: 2px 2px 8px 2px;
        opacity: 0;
        transform: scale(0.6);
        transition: 0.2s ease-out;
      }

      &.checked {
        border-color: colors.$main-color;
        background: colors.$main-color;

        &::after {
          opacity: 1;
          transform: rotate(45deg) scale(1);
        }
      }
    }
  }
}

.mandatory-field {
  margin-top: 20px;
  color: colors.$darkgrey-color;
  font-size: 14px;

  .required {
    margin-right: 5px;
    color: colors.$blue-color;
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
  .container {
    margin: 42px 0 !important;
  }

  .form {
    flex-direction: column !important;
    .column {
      margin: 0 !important;
    }
  }
}
</style>
